import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import Modal from "../Modal";
import IModalidadPago from "../../interfaces/IModalidadPago";
import { recaudadoraLabel } from "../../hooks/usePagoOnline.hook";

interface Props {
  show: boolean;
  onClose: () => void;
  modalidades: IModalidadPago[];
  loadingModalidades?: boolean;
  payingToken?: string | null;
  error?: string;
  onSelectModalidad: (modalidad: IModalidadPago) => void;
}

// "MASTERCARD - " → "MASTERCARD"
const cleanModalidad = (m: string) => (m || "").replace(/\s*-\s*$/, "").trim();

export default function ModalMediosPago({
  show,
  onClose,
  modalidades,
  loadingModalidades,
  payingToken,
  error,
  onSelectModalidad,
}: Props) {
  const [selectedModalidad, setSelectedModalidad] = useState("");
  const [selectedBanco, setSelectedBanco] = useState("");

  const isPaying = payingToken != null;

  // MODALIDAD distintas (en orden de aparición) para el combo.
  const modalidadOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    modalidades.forEach((m) => {
      if (!seen.has(m.MODALIDAD)) {
        seen.add(m.MODALIDAD);
        out.push(m.MODALIDAD);
      }
    });
    return out;
  }, [modalidades]);

  useEffect(() => {
    if (modalidadOptions.length === 1) {
      setSelectedModalidad(modalidadOptions[0]);
    } else {
      setSelectedModalidad("");
    }
  }, [modalidadOptions]);

  const filtered = useMemo(
    () => modalidades.filter((m) => m.MODALIDAD === selectedModalidad),
    [modalidades, selectedModalidad]
  );

  // Segundo nivel: BANCO/tarjeta (solo cuando la modalidad trae ese prefiltro).
  const bancoOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    filtered.forEach((m) => {
      const b = m.BANCO;
      if (b && b.trim() !== "" && !seen.has(b)) {
        seen.add(b);
        out.push(b);
      }
    });
    return out;
  }, [filtered]);

  const hasBanco = bancoOptions.length > 0;

  // Al cambiar la modalidad (cambia bancoOptions): autoseleccionar si hay una sola, sino resetear.
  useEffect(() => {
    setSelectedBanco(bancoOptions.length === 1 ? bancoOptions[0] : "");
  }, [bancoOptions]);

  const finalOptions = useMemo(
    () => (hasBanco ? filtered.filter((m) => m.BANCO === selectedBanco) : filtered),
    [hasBanco, filtered, selectedBanco]
  );

  return (
    <Modal title="Elegí cómo pagar" onClose={onClose} show={show}>
      {loadingModalidades ? (
        <div className="text-center my-4">
          <FontAwesomeIcon icon={faSpinner} className="spinner" /> Cargando
          medios de pago…
        </div>
      ) : (
        <div className="mxd-pay-methods">
          {error && <div className="mxd-drawer-error">{error}</div>}

          {modalidades.length === 0 ? (
            <p className="text-center my-3">
              No hay medios de pago disponibles.
            </p>
          ) : (
            <>
              <label className="mxd-pay-label" htmlFor="mxd-modalidad-cuotas">
                Medio de pago
              </label>
              <select
                id="mxd-modalidad-cuotas"
                className="mxd-pay-select"
                value={selectedModalidad}
                onChange={(e) => setSelectedModalidad(e.target.value)}
                disabled={isPaying}
              >
                <option value="">Seleccioná un medio…</option>
                {modalidadOptions.map((mod) => (
                  <option key={mod} value={mod}>
                    {cleanModalidad(mod)}
                  </option>
                ))}
              </select>

              {selectedModalidad && hasBanco && (
                <>
                  <label className="mxd-pay-label" htmlFor="mxd-banco-cuotas">
                    Tarjeta
                  </label>
                  <select
                    id="mxd-banco-cuotas"
                    className="mxd-pay-select"
                    value={selectedBanco}
                    onChange={(e) => setSelectedBanco(e.target.value)}
                    disabled={isPaying}
                  >
                    <option value="">Seleccioná una tarjeta…</option>
                    {bancoOptions.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </>
              )}

              {selectedModalidad && (!hasBanco || selectedBanco) && (
                <div className="mxd-pay-options">
                  {finalOptions.map((m) => {
                    const loading = payingToken === m.TOKEN;
                    const primary =
                      m.CUOTAS?.trim() ||
                      recaudadoraLabel(
                        m.RECAUDADORA,
                        cleanModalidad(m.MODALIDAD)
                      );
                    const secondary = m.DETALLE;
                    return (
                      <button
                        key={m.TOKEN}
                        type="button"
                        className="mxd-pay-option"
                        onClick={() => onSelectModalidad(m)}
                        disabled={isPaying}
                      >
                        {loading ? (
                          <span className="mxd-pay-option-main">
                            <FontAwesomeIcon
                              icon={faSpinner}
                              className="spinner"
                            />{" "}
                            Procesando…
                          </span>
                        ) : (
                          <>
                            <span className="mxd-pay-option-left">
                              <span className="mxd-pay-option-main">
                                {primary}
                              </span>
                              {secondary && (
                                <span className="mxd-pay-option-sub">
                                  {secondary}
                                </span>
                              )}
                            </span>
                            {m.TOTAL && (
                              <span className="mxd-pay-option-total">
                                <span className="mxd-pay-option-total-label">
                                  Total Final:
                                </span>
                                <span className="mxd-pay-option-total-value">
                                  {m.TOTAL}
                                </span>
                              </span>
                            )}
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
